import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelected, useFocused } from 'slate-react';
import { Transforms } from 'slate';
import { Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { Today } from '@material-ui/icons';
import _e from 'common/localeUtil';

export const DATE_MENTION_TYPE = 'dateMention';

export const withDateMentions = (editor) => {
	const { isInline, isVoid } = editor;

	editor.isInline = (element) => {
		return element.type === DATE_MENTION_TYPE ? true : isInline(element);
	};

	editor.isVoid = (element) => {
		return element.type === DATE_MENTION_TYPE ? true : isVoid(element);
	};

	editor.insertDateMention = (mention) => {
		const mentionObj = { type: DATE_MENTION_TYPE, mention, children: [{ text: '' }] };
		Transforms.insertNodes(editor, mentionObj);
		Transforms.move(editor);
	};

	editor.updateSelectedDateMention = (mention) => {
		Transforms.setNodes(
			editor,
			{
				mention,
			},
			{
				at: editor.selection,
				match: (node) => node.type === DATE_MENTION_TYPE,
			},
		);
	};

	return editor;
};

// The two date-detection patterns below are assembled from named sub-patterns
// rather than written as single monolithic literals. Each piece is small enough
// to read on its own, and composing them keeps any single pattern well under the
// regex cognitive-complexity limit. String.raw is used so the fragments are
// copied verbatim (backslashes included) into the assembled RegExp.

// --- "L" numeric date, e.g. "07/06/2026" (M/D/YYYY) ---
const L_YEAR = String.raw`(19|[2-9]\d)\d{2}`;
const L_LEAP_YEAR = String.raw`((19|[2-9]\d)(0[48]|[2468][048]|[13579][26])|(([2468][048]|[3579][26])00))`;
const L_MONTH_DAY_1_28 = String.raw`(0?[1-9]|1[012])/(0?[1-9]|1\d|2[0-8])`;
const L_MONTH_DAY_29_30 = String.raw`(0?[13456789]|1[012])/(29|30)`;
const L_MONTH_DAY_31 = String.raw`(0?[13578]|1[02])/31`;
const L_DATE = `(${L_MONTH_DAY_1_28}|${L_MONTH_DAY_29_30}|${L_MONTH_DAY_31})/${L_YEAR}|0?2/29/${L_LEAP_YEAR}`;
export const DATE_MENTIONS_L_CHECK_REGEX = new RegExp(`^(${L_DATE})$`, 'gm');

// export const DATE_MENTIONS_LT_CHECK_REGEX = /((([0]?[1-9]|1[0-2])(:|\.)[0-5][0-9]((:|\.)[0-5][0-9])?( )?(AM|am|aM|Am|PM|pm|pM|Pm))|(([0]?[0-9]|1[0-9]|2[0-3])(:|\.)[0-5][0-9]((:|\.)[0-5][0-9])?))$/gm;

// --- "LL" long date, e.g. "6 July 2026" (D MMMM YYYY) ---
const LL_DAY = String.raw`(31(?! (Feb(ruary)?|Apr(il)?|June?|(Sep(?=\b|t)t?|Nov)(ember)?)))|((30|29)(?!\\ Feb(ruary)?))|(29(?=\\ Feb(ruary)?\\ (((1[6-9]|[2-9]\d)(0[48]|[2468][048]|[13579][26])|((16|[2468][048]|[3579][26])00)))))|(0?[1-9])|1\d|2[0-8]`;
const LL_MONTH = String.raw`Jan(uary)?|Feb(ruary)?|Ma(r(ch)?|y)|Apr(il)?|Ju((ly?)|(ne?))|Aug(ust)?|Oct(ober)?|(Sep(?=\b|t)t?|Nov|Dec)(ember)?`;
const LL_YEAR = String.raw`(1[6-9]|[2-9]\d)\d{2}`;
const LL_DATE = String.raw`(${LL_DAY})\\ (${LL_MONTH}) (${LL_YEAR})`;
export const DATE_MENTIONS_LL_CHECK_REGEX = new RegExp(`${LL_DATE}$`, 'gm');

export const DATE_MENTION_REGEXS = [DATE_MENTIONS_L_CHECK_REGEX, DATE_MENTIONS_LL_CHECK_REGEX];

const useStyles = makeStyles((theme) => ({
	mention: {
		// padding: '3px 3px 2px',
		verticalAlign: 'baseline',
		display: 'inline-block',
		borderRadius: theme.spacing(0.5),
		boxShadow: 'none',
		borderBottom: '1px dashed #999',
	},
	selected: {
		boxShadow: '0 0 0 2px #B4D5FF',
	},
	icon: {
		fontSize: 14,
		marginLeft: 3,
	},
	hasMention: {
		color: '#536DFE',
		fontWeight: '600',
	},
}));

const DateMentionElement = ({ attributes = {}, children, element, setSelectedMention }) => {
	const classes = useStyles();
	const selected = useSelected();
	const focused = useFocused();
	useEffect(() => {
		if (typeof setSelectedMention === 'function') {
			if (selected && focused) {
				setSelectedMention(element.mention);
			} else {
				setSelectedMention(null);
			}
		}
	}, [selected, element.mention, focused]);

	const { mention } = element;
	const tooltipTitle = _e('common.create_reminder_tooltip');
	return (
		<Tooltip title={tooltipTitle}>
			<span
				{...attributes}
				contentEditable={false}
				className={clsx(classes.mention, {
					[classes.selected]: selected && focused,
					[classes.hasMention]: mention.id,
				})}
			>
				{mention.date}
				{children}
				{mention.id && <Today className={classes.icon} />}
			</span>
		</Tooltip>
	);
};

DateMentionElement.propTypes = {
	children: PropTypes.element,
	element: PropTypes.shape({
		mention: PropTypes.object,
	}),
	attributes: PropTypes.object,
	setSelectedMention: PropTypes.func,
};

export { DateMentionElement };
